import React from 'react';
import { APP_CONFIG } from '../../config/appConfig';

interface PlayStoreBadgeProps {
  variant?: 'badge-dark' | 'badge-light' | 'pill' | 'compact';
  className?: string;
  forceShow?: boolean;
  utmCampaign?: string;
}

export const PlayStoreBadge: React.FC<PlayStoreBadgeProps> = ({
  variant = 'badge-dark',
  className = '',
  forceShow = false,
  utmCampaign = 'landing_page'
}) => {
  if (!APP_CONFIG.ENABLE_GOOGLE_PLAY_STORE && !forceShow) {
    return null;
  }

  const playUrl = `${APP_CONFIG.GOOGLE_PLAY_URL}${
    APP_CONFIG.GOOGLE_PLAY_URL.includes('?') ? '&' : '?'
  }utm_source=rmax_web&utm_medium=badge&utm_campaign=${encodeURIComponent(utmCampaign)}`;

  // SVG do Ícone Oficial da Google Play
  const GooglePlayIcon = () => (
    <svg 
      className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" 
      viewBox="0 0 512 512" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path 
        d="M325.3 234.3L104.6 13l280.8 161.2-60.1 59.9z" 
        fill="#00F076"
      />
      <path 
        d="M26.2 31.7C20 37.9 16 46.8 16 57.5v397c0 10.7 4 19.6 10.2 25.8l239.5-245.9L26.2 31.7z" 
        fill="#00E6FF"
      />
      <path 
        d="M325.3 277.7l60.1 59.9L104.6 499l220.7-221.3z" 
        fill="#FF3A44"
      />
      <path 
        d="M481.5 233.1l-96.1-55.2-60.1 56.4 60.1 59.9 96.1-55.2c16.3-9.4 16.3-24.8 0-36.2l-.0.1z" 
        fill="#FFC800"
      />
    </svg>
  );

  if (variant === 'pill') {
    return (
      <a
        href={playUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2.5 px-4 py-2 bg-zinc-900/90 hover:bg-zinc-800 text-white border border-white/15 rounded-full text-xs font-semibold shadow-lg hover:border-emerald-500/50 hover:shadow-emerald-500/10 transition-all duration-300 active:scale-95 group ${className}`}
        aria-label="Baixe o aplicativo R-Max no Google Play"
      >
        <GooglePlayIcon />
        <div className="flex flex-col text-left leading-none">
          <span className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold group-hover:text-emerald-400 transition-colors">
            Disponível no
          </span>
          <span className="text-xs font-black text-white">Google Play</span>
        </div>
      </a>
    );
  }

  if (variant === 'compact') {
    return (
      <a
        href={playUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700 rounded-xl text-xs font-medium transition-all hover:scale-105 active:scale-95 ${className}`}
        aria-label="Disponível no Google Play"
      >
        <GooglePlayIcon />
        <span className="text-[11px] font-bold">Google Play</span>
      </a>
    );
  }

  if (variant === 'badge-light') {
    return (
      <a
        href={playUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 hover:border-slate-400 rounded-2xl shadow-sm transition-all duration-300 hover:-translate-y-0.5 active:scale-95 group ${className}`}
        aria-label="Disponível no Google Play"
      >
        <GooglePlayIcon />
        <div className="flex flex-col text-left leading-tight">
          <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold group-hover:text-emerald-600 transition-colors">
            DISPONÍVEL NO
          </span>
          <span className="text-sm font-black text-slate-900 tracking-wide">
            Google Play
          </span>
        </div>
      </a>
    );
  }

  // Variant default / badge-dark
  return (
    <a
      href={playUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-900 text-white border border-zinc-800 hover:border-zinc-600 rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-0.5 active:scale-95 group ${className}`}
      aria-label="Disponível no Google Play"
    >
      <GooglePlayIcon />
      <div className="flex flex-col text-left leading-tight">
        <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold group-hover:text-emerald-400 transition-colors">
          DISPONÍVEL NO
        </span>
        <span className="text-sm font-black text-white tracking-wide">
          Google Play
        </span>
      </div>
    </a>
  );
};
